import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { adminRoutes } from "../../../routers/AdminRouters";
import {
  useDeleteSubscriberMutation,
  useGetSubscribersQuery,
  useChangeSubscriberStatusMutation,
  useGetSubscriberDashboardTokenMutation,
} from "../../../services";
import { SubscriberRow } from "../../../types";

const SubscriberList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Pagination state for infinite scroll
  const [page, setPage] = useState(1);
  const limit = 10;
  const [allSubscribers, setAllSubscribers] = useState<SubscriberRow[]>([]);

  // Load from RTK with pagination
  const {
    data: subscribersData,
    isLoading,
    isFetching,
    error,
  } = useGetSubscribersQuery({ page, limit });
  const [deleteSubscriber, { isLoading: isDeleting }] =
    useDeleteSubscriberMutation();
  const [changeSubscriberStatus, { isLoading: isChangingStatus }] =
    useChangeSubscriberStatusMutation();
  const [getSubscriberDashboardToken, { isLoading: isGettingDashboardToken }] =
    useGetSubscriberDashboardTokenMutation();

  // State for view-subscriber dashboard confirmation modal
  const [viewTarget, setViewTarget] = useState<SubscriberRow | null>(null);
  const openViewModal = (row: SubscriberRow) => setViewTarget(row);
  const closeViewModal = () => setViewTarget(null);

  // State for status-change confirmation modal
  const [statusTarget, setStatusTarget] = useState<SubscriberRow | null>(null);
  const openStatusModal = (row: SubscriberRow) => setStatusTarget(row);
  const closeStatusModal = () => setStatusTarget(null);

  const confirmStatusChange = async () => {
    if (!statusTarget) return;
    const isCurrentlyActive = statusTarget.status === "Active";
    const nextStatus = !isCurrentlyActive;

    try {
      await changeSubscriberStatus({
        subscriberid: statusTarget.id,
        status: nextStatus,
      }).unwrap();

      // Optimistically update local list status
      setAllSubscribers((prev) =>
        prev.map((subscriber) =>
          subscriber.id === statusTarget.id
            ? {
              ...subscriber,
              status: nextStatus ? "Active" : "Inactive",
            }
            : subscriber,
        ),
      );
    } catch (e) {
      console.error("Change subscriber status failed:", e);
    } finally {
      closeStatusModal();
    }
  };

  const confirmViewDashboard = () => {
    if (!viewTarget) return;

    (async () => {
      try {
        const res = await getSubscriberDashboardToken({ subscriberid: viewTarget.id }).unwrap();
        const token =
          (res as any)?.token ||
          (res as any)?.result ||
          (res as any)?.data?.token;

        if (token) {
          const url = `${import.meta.env.VITE_SUBSCRIBER_APP_URL}/login-token/${token}`;
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          console.error("No token returned for subscriber dashboard");
        }
      } catch (e) {
        console.error("Failed to get subscriber dashboard token:", e);
      } finally {
        closeViewModal();
      }
    })();
  };

  // Current page rows from API
  const currentPageRows: SubscriberRow[] = Array.isArray(subscribersData)
    ? (subscribersData as any[]).map((s: any) => ({
      id: String(s._id ?? s.id ?? ""),
      name: s.name ?? "",
      email: s.email ?? s.contact?.email ?? "",
      phone: String(s.phone ?? ""),
      status: s.status === "Active" ? "Active" : "Inactive",
    }))
    : [];

  // Append new page data to allSubscribers
  React.useEffect(() => {
    if (!currentPageRows || currentPageRows.length === 0) return;
    setAllSubscribers((prev) =>
      page === 1 ? currentPageRows : [...prev, ...currentPageRows],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribersData, page]);

  // Infinite scroll: load next page when reaching bottom
  React.useEffect(() => {
    const container = document.getElementById("subscriber-table-scroll");
    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 40
      ) {
        if (!isFetching && currentPageRows.length === limit) {
          setPage((prev) => prev + 1);
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [currentPageRows.length, isFetching, limit]);

  // THEME CLASSES
  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
  const heading = "text-text";
  const crumbLink = theme === "dark" ? "text-gray-500" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

  const cardClass =
    theme === "dark"
      ? "bg-[#1A1A1A] border-0 text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const cardHeader =
    theme === "dark"
      ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between"
      : "px-6 py-4 border-b border-gray-200 text-gray-800 flex items-center justify-between";

  const thClass =
    theme === "dark"
      ? "px-6 py-3 text-left text-sm font-bold text-[#F3F3F6]  tracking-wider border-b border-[#2A2A2A]"
      : "px-6 py-3 text-left text-sm font-bold text-gray-700  tracking-wider border-b border-gray-200";

  const tdClass =
    theme === "dark"
      ? "px-6 py-4 text-sm text-text-table border-b border-[#2A2A2A]"
      : "px-6 py-4 text-sm text-text-table border-b border-gray-200";

  const iconHover = theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5";
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-6">
        {/* Heading change */}
        <h1 className={`text-lg font-medium ${heading} mb-2`}>
          Subscriber Overview
        </h1>
        <div className="text-sm flex items-center gap-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
              stroke="#ADADAD"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Link
            to="/"
            className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
          >
            Dashboard
          </Link>
          <span className={crumbSep}>&gt;</span>
          {/* Breadcrumb label change */}
          <span className="text-[#B6CB24] font-normal">All Subscribers</span>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden shadow-lg`}>
        <div className={cardHeader}>
          {/* Card header label change */}
          <span className="text-base font-medium">All Subscriber details</span>

          {/* Button text/route change */}
          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#B6CB24] text-[#B6CB24] hover:bg-[#B6CB24]/10 text-sm font-medium"
            onClick={() => navigate(adminRoutes.subscriberCreate)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2.00012C17.5228 2.00012 22 6.47727 22 12.0001C22 17.523 17.5228 22.0001 12 22.0001C6.47715 22.0001 2 17.523 2 12.0001M8.909 2.48699C7.9 2.8146 6.96135 3.29828 6.12153 3.90953M3.90943 6.12162C3.29806 6.9616 2.81432 7.90044 2.4867 8.90964"
                stroke="#F3F3F6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 8.00012V16.0001M16 12.0001H8"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {/* ...existing plus icon... */}
            Create new Subscriber
          </button>
        </div>

        <div
          className="relative overflow-x-auto"
          style={{ maxHeight: "500px", overflowY: "auto" }}
          id="subscriber-table-scroll"
        >
          <table className="min-w-full">
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                background: theme === "dark" ? "#1A1A1A" : "#fff",
              }}
            >
              <tr>
                {/* Table headers updated to match the image */}
                <th className={thClass}>Name</th>
                <th className={thClass}>Email</th>
                <th className={thClass}>Phone number</th>
                <th className={thClass}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading || (isFetching && page === 1) ? (
                <tr>
                  {/* Loading copy change */}
                  <td className={`${tdClass} text-center`} colSpan={4}>
                    Loading subscribers...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  {/* Error copy change */}
                  <td className={`${tdClass} text-center`} colSpan={4}>
                    Failed to load subscribers.
                  </td>
                </tr>
              ) : allSubscribers.length === 0 ? (
                <tr>
                  {/* Empty copy change */}
                  <td className={`${tdClass} text-center`} colSpan={4}>
                    No subscribers found.
                  </td>
                </tr>
              ) : (
                allSubscribers.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      theme === "dark"
                        ? "hover:bg-[#222222]"
                        : "hover:bg-gray-50"
                    }
                  >
                    {/* Cells updated to staff fields */}
                    <td className={tdClass}>{r.name}</td>
                    <td className={tdClass}>{r.email}</td>
                    <td className={tdClass}>{r.phone}</td>

                    {/* Status */}

                    <td className={tdClass}>
                      <div className="flex items-center gap-2">
                        <button
                          title="View subscriber dashboard"
                          className={`p-1.5 rounded transition-colors ${iconHover}`}
                          onClick={() => openViewModal(r)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M20.9977 12.5032C20.9977 12.5032 21 12.0338 21 11.5029C21 7.02321 21 4.78334 19.6088 3.39167C18.2175 2 15.9783 2 11.5 2C7.02166 2 4.78249 2 3.39124 3.39167C2 4.78334 2 7.02321 2 11.5029C2 15.9827 2 18.2225 3.39124 19.6142C4.55785 20.7812 6.32067 20.9696 9.5 21"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              d="M2 7H21"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              opacity="0.4"
                              d="M6 16H7"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10 12H15M6 12H7"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M17 18.5H17.009"
                              stroke="#C6D92C"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M21.772 18.0225C21.924 18.2357 22 18.3422 22 18.5C22 18.6578 21.924 18.7643 21.772 18.9775C21.089 19.9353 19.3446 22 17 22C14.6554 22 12.911 19.9353 12.228 18.9775C12.076 18.7643 12 18.6578 12 18.5C12 18.3422 12.076 18.2357 12.228 18.0225C12.911 17.0647 14.6554 15 17 15C19.3446 15 21.089 17.0647 21.772 18.0225Z"
                              stroke="#C6D92C"
                              strokeWidth="1.5"
                            />
                          </svg>
                        </button>
                        <button
                          title="block subscriber"
                          className="p-1.5 rounded hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => openStatusModal(r)}
                          disabled={isChangingStatus}
                        >
                          {r.status === "Active" ? (
                            // Active icon (second SVG)
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M14.5019 8C14.5019 5.23858 12.2634 3 9.50195 3C6.74053 3 4.50195 5.23858 4.50195 8C4.50195 10.7614 6.74053 13 9.50195 13C12.2634 13 14.5019 10.7614 14.5019 8Z"
                                stroke="#C6D92C"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M16.2375 15.6747V14.2523C16.2375 13.2845 17.022 12.5 17.9898 12.5C18.4669 12.5 18.8995 12.6907 19.2155 13M16.307 21H19.693C20.6899 21 21.498 20.1934 21.498 19.1984V17.6947C21.498 16.6997 20.6899 15.8931 19.693 15.8931H16.307C15.3102 15.8931 14.502 16.6997 14.502 17.6947V19.1984C14.502 20.1934 15.3102 21 16.307 21Z"
                                stroke="#C6D92C"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M2.50195 20C2.50195 16.134 5.63596 13 9.50195 13C10.9892 13 12.3681 13.4638 13.5019 14.2547"
                                stroke="#C6D92C"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            // Inactive / blocked icon (first SVG)
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M13.25 17.25C13.25 14.7647 15.2647 12.75 17.75 12.75C20.2353 12.75 22.25 14.7647 22.25 17.25C22.25 19.7353 20.2353 21.75 17.75 21.75C15.2647 21.75 13.25 19.7353 13.25 17.25ZM15.4222 16.3364C15.311 16.6194 15.25 16.9276 15.25 17.25C15.25 18.6307 16.3693 19.75 17.75 19.75C18.0724 19.75 18.3806 19.689 18.6636 19.5778L15.4222 16.3364ZM16.8364 14.9222L20.0778 18.1636C20.189 17.8806 20.25 17.5724 20.25 17.25C20.25 15.8693 19.1307 14.75 17.75 14.75C17.4276 14.75 17.1194 14.811 16.8364 14.9222Z"
                                fill="#D92626"
                              />
                              <path
                                d="M13.5096 13.3665C12.5721 14.3896 12 15.753 12 17.25C12 18.567 12.4428 19.7806 13.1876 20.75H2.5C2.08579 20.75 1.75 20.4142 1.75 20C1.75 16.3234 4.31022 13.2447 7.7451 12.4495C5.70857 11.7274 4.25 9.78405 4.25 7.5C4.25 4.60051 6.6005 2.25 9.5 2.25C12.3995 2.25 14.75 4.60051 14.75 7.5C14.75 9.78405 13.2914 11.7274 11.2549 12.4495C12.062 12.6364 12.8209 12.9493 13.5096 13.3665Z"
                                fill="#D92626"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View subscriber dashboard confirmation modal */}
      {viewTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"
            }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark"
              ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
              : "bg-white text-gray-900 border-gray-200"
              }`}
          >
            <button
              onClick={closeViewModal}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
                }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>
            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20.9977 12.5032C20.9977 12.5032 21 12.0338 21 11.5029C21 7.02321 21 4.78334 19.6088 3.39167C18.2175 2 15.9783 2 11.5 2C7.02166 2 4.78249 2 3.39124 3.39167C2 4.78334 2 7.02321 2 11.5029C2 15.9827 2 18.2225 3.39124 19.6142C4.55785 20.7812 6.32067 20.9696 9.5 21"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M2 7H21"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 16H7M10 12H15M6 12H7"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 18.5H17.009"
                    stroke="#C6D92C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21.772 18.0225C21.924 18.2357 22 18.3422 22 18.5C22 18.6578 21.924 18.7643 21.772 18.9775C21.089 19.9353 19.3446 22 17 22C14.6554 22 12.911 19.9353 12.228 18.9775C12.076 18.7643 12 18.6578 12 18.5C12 18.3422 12.076 18.2357 12.228 18.0225C12.911 17.0647 14.6554 15 17 15C19.3446 15 21.089 17.0647 21.772 18.0225Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                View Subscriber Dashboard
              </h3>
              <p
                className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
              >
                Access subscriber data and subscription details.
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmViewDashboard}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isGettingDashboardToken}
                >
                  {isGettingDashboardToken ? "Opening..." : "Access"}
                </button>
                <button
                  type="button"
                  onClick={closeViewModal}
                  className="px-4 py-2 font-medium hover:opacity-90 rounded-[10px] border"
                  style={{
                    border:
                      theme === "dark"
                        ? "1px solid #3F3F3F"
                        : "1px solid #E5E7EB",
                    background: theme === "dark" ? "#111111" : "#F3F4F6",
                    color: theme === "dark" ? "#E5E7EB" : "#111827",
                  }}
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status change confirmation modal */}
      {statusTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"
            }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark"
              ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
              : "bg-white text-gray-900 border-gray-200"
              }`}
          >
            <button
              onClick={closeStatusModal}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
                }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>{" "}
            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M14.5 8C14.5 5.23858 12.2614 3 9.5 3C6.73858 3 4.5 5.23858 4.5 8C4.5 10.7614 6.73858 13 9.5 13C12.2614 13 14.5 10.7614 14.5 8Z"
                    stroke="#D92626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.5 20C2.5 16.134 5.63401 13 9.5 13C10.5736 13 11.5907 13.2417 12.5 13.6736"
                    stroke="#D92626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.55 15.05L20.45 19.95M21.5 17.5C21.5 15.567 19.933 14 18 14C16.067 14 14.5 15.567 14.5 17.5C14.5 19.433 16.067 21 18 21C19.933 21 21.5 19.433 21.5 17.5Z"
                    stroke="#D92626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Block this subscriber?{" "}
              </h3>
              <p
                className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
              >
                Blocking will stop all subscriptions.{" "}
                {statusTarget.status === "Active"
                  ? " deactivate "
                  : " activate "}
                "{statusTarget.name}"?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmStatusChange}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isChangingStatus}
                >
                  {isChangingStatus ? "Updating..." : "Confirm"}
                </button>
                <button
                  type="button"
                  onClick={closeStatusModal}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isChangingStatus}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriberList;
